const STORAGE_KEY = 'escribonauta-offline-projects'

/** Metadata for a project the user marked available without a connection. */
export interface OfflineProjectRecord {
  projectId: string
  title: string
  type: string
  status: string
  /** ISO time when the user marked the project available offline. */
  availableSince: string
  /** ISO time of the last successful mirror refresh. */
  lastSyncedAt: string
  /** Latest known server change among mirrored chapters (ISO). */
  lastServerChangeAt?: string
}

function readAll(): OfflineProjectRecord[] {
  if (typeof localStorage === 'undefined') return []

  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return []
    const parsed = JSON.parse(raw) as OfflineProjectRecord[]
    if (!Array.isArray(parsed)) return []
    return parsed.filter((record) => record && typeof record.projectId === 'string')
  } catch (error) {
    console.error('Error reading offline projects:', error)
    return []
  }
}

function writeAll(records: OfflineProjectRecord[]): void {
  if (typeof localStorage === 'undefined') return

  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(records))
  } catch (error) {
    console.error('Error writing offline projects:', error)
  }
}

export function listOfflineProjects(): OfflineProjectRecord[] {
  return readAll()
}

export function getOfflineProject(projectId: string): OfflineProjectRecord | null {
  if (!projectId) return null
  return readAll().find((record) => record.projectId === projectId) ?? null
}

export function isProjectAvailableOffline(projectId: string): boolean {
  return getOfflineProject(projectId) !== null
}

export function markProjectAvailableOffline(input: {
  projectId: string
  title: string
  type: string
  status: string
  lastServerChangeAt?: string
}): OfflineProjectRecord {
  const now = new Date().toISOString()
  const existing = getOfflineProject(input.projectId)
  const next: OfflineProjectRecord = {
    projectId: input.projectId,
    title: input.title,
    type: input.type,
    status: input.status,
    availableSince: existing?.availableSince || now,
    lastSyncedAt: now,
    lastServerChangeAt: input.lastServerChangeAt || existing?.lastServerChangeAt,
  }
  const records = readAll().filter((record) => record.projectId !== input.projectId)
  records.push(next)
  writeAll(records)
  return next
}

export function touchOfflineProjectSync(
  projectId: string,
  patch: { lastSyncedAt?: string; lastServerChangeAt?: string; title?: string; status?: string },
): void {
  const existing = getOfflineProject(projectId)
  if (!existing) return

  const next: OfflineProjectRecord = {
    ...existing,
    ...patch,
  }
  const records = readAll().map((record) => (record.projectId === projectId ? next : record))
  writeAll(records)
}

export function removeOfflineProject(projectId: string): void {
  writeAll(readAll().filter((record) => record.projectId !== projectId))
}
